// components/Chatbot/ChatbotIcon.tsx
import React from 'react';
import { IconButton, Badge, Box, keyframes, useTheme } from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CloseIcon from '@mui/icons-material/Close';

interface ChatbotIconProps {
  isOpen: boolean;
  onClick: () => void;
  hasNewMessages?: boolean;
}

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(var(--pulse-color), 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(var(--pulse-color), 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(var(--pulse-color), 0);
  }
`;

const ChatbotIcon: React.FC<ChatbotIconProps> = ({ isOpen, onClick, hasNewMessages }) => {
  const theme = useTheme();

  const getRGBColor = (color: string) => {
    // Convert hex to RGB
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `${r}, ${g}, ${b}`;
  };

  return (
    <Box
      sx={{
        // position: 'fixed',
        // bottom: 24,
        // right: 24,
        zIndex: 9999,
        '--pulse-color': getRGBColor(theme.palette.primary.main),
        animation: !isOpen ? `${pulse} 2s infinite` : 'none',
      }}
    >
      <IconButton
        onClick={onClick}
        sx={{
          width: 48,
          height: 48,
          backgroundColor: isOpen ? theme.palette.background.paper : theme.palette.primary.main,
          backdropFilter: 'blur(10px)',
          border: `2px solid rgba(${getRGBColor(theme.palette.primary.main)}, 0.3)`,
          boxShadow: `0 8px 32px rgba(${getRGBColor(theme.palette.primary.dark)}, 0.2)`,
          '&:hover': {
            backgroundColor: isOpen ? theme.palette.background.paper : theme.palette.primary.main,
            transform: 'scale(1.1)',
          },
          transition: 'all 0.3s ease',
        }}
      >
        {hasNewMessages && !isOpen && (
          <Badge
            color="secondary"
            variant="dot"
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              '& .MuiBadge-badge': {
                backgroundColor: theme.palette.secondary.main,
              },
            }}
          />
        )}
        {isOpen ? (
          <CloseIcon sx={{ color: theme.palette.primary.main, fontSize: 32 }} />
        ) : (
          <SmartToyIcon sx={{ color: theme.palette.primary.contrastText, fontSize: 32 }} />
        )}
      </IconButton>
    </Box>
  );
};

export default ChatbotIcon;
